/* Campaigns — multi-step cadence orchestration
 *
 * Rewritten in v0.7.1 to support real cadence engines instead of the naive
 * string-list placeholder. Users can define ordered STEPS with:
 *   - channel  (LinkedIn Connect · LinkedIn DM · Email · WhatsApp)
 *   - delay_days
 *   - body template with {{first_name}}, {{company}} variables (LLM personalises on top)
 *   - subject line (email only)
 *   - LinkedIn Connect wait_days before falling through
 * Plus stop-conditions, auto-ICP-enrollment, tone-of-voice.
 *
 * Three-view flow:
 *  1) List (cards grid)  →  click card
 *  2) Detail drawer      →  cadence timeline + enrolments + stats + start/pause
 *  3) Builder modal      →  create/edit meta + steps
 */
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "./Dashboard";
import ScaliqCampaignsPanel from "@/components/sales/ScaliqCampaignsPanel";
import { toast } from "sonner";
import {
    Megaphone, Plus, Play, Pause, Trash2, X, Loader2, Users,
    Linkedin, Mail, MessageCircle, UserPlus, GripVertical,
    Save, Edit3, ArrowRight, Clock, StopCircle, Send,
    CheckCircle2, AlertTriangle, ChevronDown,
} from "lucide-react";

// ═════════════════════════════════════════════════════════════════════════
const CHANNEL_META = {
    linkedin_connect: { label: "LinkedIn Connect", Icon: UserPlus, tint: "#0a66c2" },
    linkedin_dm:      { label: "LinkedIn Bericht", Icon: Linkedin, tint: "#0a66c2" },
    email:            { label: "Email",            Icon: Mail,     tint: "#e04b26" },
    whatsapp:         { label: "WhatsApp",         Icon: MessageCircle, tint: "#25d366" },
};

// ═════════════════════════════════════════════════════════════════════════
export default function Campaigns({ defaultType = "" }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showBuilder, setShowBuilder] = useState(false);
    const [editing, setEditing] = useState(null);      // campaign being edited
    const [openDetail, setOpenDetail] = useState(null); // campaign id in detail drawer
    const [typeFilter, setTypeFilter] = useState(defaultType);

    // Reset filter when the entry-route defaultType changes (Sales ↔ Recruit sidebar)
    useEffect(() => { setTypeFilter(defaultType); }, [defaultType]);

    const load = async () => {
        setLoading(true);
        const r = await api.get("/campaigns");
        setRows(r.data || []);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(
        () => rows.filter((c) => !typeFilter || c.type === typeFilter),
        [rows, typeFilter],
    );

    const openNew = () => { setEditing(null); setShowBuilder(true); };
    const openEdit = (c) => { setEditing(c); setShowBuilder(true); };

    return (
        <PageHeader
            title={t("campaigns.title")}
            subtitle={t("campaigns.subtitle")}
            actions={
                <button onClick={openNew} data-testid="new-campaign-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] transition-colors">
                    <Plus size={14} /> {t("campaigns.new_campaign")}
                </button>
            }
        >
            {/* Type filter — navigeert naar /app/sales/campaigns of /app/recruit/campaigns
                zodat de sidebar de juiste sectie highlight (nav.isActive kickt in). */}
            <div className="mb-4 flex gap-1">
                {[
                    { v: "", l: i18n.t("common.all"), path: "/app/campaigns" },
                    { v: "sales", l: "Sales", path: "/app/sales/campaigns" },
                    { v: "recruitment", l: "Recruitment", path: "/app/recruit/campaigns" },
                ].map(({ v, l, path }) => (
                    <button key={v} onClick={() => { setTypeFilter(v); navigate(path); }}
                        data-testid={`filter-type-${v || "all"}`}
                        className={`text-xs px-3 py-1.5 rounded-full transition-colors ${typeFilter === v
                            ? "bg-[var(--jr-brand)] text-black"
                            : "border border-white/10 text-[var(--jr-text-3)] hover:text-white hover:bg-white/5"}`}>
                        {l}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-[var(--jr-text-2)]">{i18n.t("common.loading")}</div>
            ) : filtered.length === 0 ? (
                <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-10 text-center text-[var(--jr-text-2)]" data-testid="campaigns-empty">
                    <Megaphone className="mx-auto text-[var(--jr-brand)]" size={28} />
                    <p className="mt-4">{i18n.t("campaigns.empty_new")}</p>
                    <button onClick={openNew} className="mt-4 inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-[var(--jr-brand)] text-black font-medium hover:bg-[var(--jr-brand-2)]">
                        <Plus size={14} /> {i18n.t("campaigns.new_campaign")}
                    </button>
                </div>
            ) : (
                <div className="grid lg:grid-cols-2 gap-4">
                    {filtered.map((c) => (
                        <CampaignCard key={c.id} c={c}
                            onOpen={() => setOpenDetail(c.id)}
                            onEdit={() => openEdit(c)} />
                    ))}
                </div>
            )}

            {/* ScalIQ LinkedIn-campagnes (read-only + lead-lijsten) */}
            <ScaliqCampaignsPanel />

            {showBuilder && (
                <CampaignBuilder
                    campaign={editing}
                    defaultType={defaultType}
                    onClose={() => { setShowBuilder(false); setEditing(null); }}
                    onSaved={() => { setShowBuilder(false); setEditing(null); load(); }}
                />
            )}

            {openDetail && (
                <CampaignDetailDrawer
                    campaignId={openDetail}
                    onClose={() => setOpenDetail(null)}
                    onEdit={(c) => { setOpenDetail(null); openEdit(c); }}
                    onChanged={load}
                />
            )}
        </PageHeader>
    );
}

// ═════════════════════════════════════════════════════════════════════════
function CampaignCard({ c, onOpen, onEdit }) {
    const rate = c.contacted ? Math.round((c.replied / c.contacted) * 100) : 0;
    const enrolled = c.stats?.enrolled || 0;
    const running = c.stats?.running || 0;
    const stepCount = (c.steps || []).length;
    return (
        <div data-testid={`campaign-${c.id}`}
            className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-6 hover:border-white/16 transition-colors">
            <div className="flex items-start justify-between gap-3">
                <button onClick={onOpen} className="text-left flex-1 min-w-0" data-testid={`campaign-open-${c.id}`}>
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{c.type}</div>
                    <h3 className="font-display text-xl tracking-tight mt-1 text-white truncate">{c.name}</h3>
                    {c.description && <p className="text-xs text-[var(--jr-text-2)] mt-1 line-clamp-2">{c.description}</p>}
                </button>
                <StatusPill status={c.status} />
            </div>

            {/* Sequence preview strip */}
            {stepCount > 0 && (
                <div className="mt-5 flex items-center gap-1.5 flex-wrap" data-testid={`sequence-preview-${c.id}`}>
                    {(c.steps || []).slice(0, 6).map((s, i) => {
                        const meta = CHANNEL_META[s.channel] || CHANNEL_META.email;
                        const Icon = meta.Icon;
                        return (
                            <React.Fragment key={s.id || i}>
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono"
                                    style={{ background: `${meta.tint}22`, color: meta.tint }}>
                                    <Icon size={10} /> {meta.label.split(" ").slice(-1)[0]}
                                    {s.delay_days > 0 && <span className="text-[var(--jr-text-3)] ml-0.5">+{s.delay_days}d</span>}
                                </span>
                                {i < Math.min(stepCount, 6) - 1 && <ArrowRight size={10} className="text-[var(--jr-text-3)]" />}
                            </React.Fragment>
                        );
                    })}
                    {stepCount > 6 && <span className="text-[10px] text-[var(--jr-text-3)]">+{stepCount - 6}</span>}
                </div>
            )}

            <div className="grid grid-cols-4 gap-3 mt-6">
                <Stat label="Steps" value={stepCount} />
                <Stat label="Enrolled" value={enrolled} />
                <Stat label={i18n.t("campaigns.stat_active")} value={running} />
                <Stat label="Reply-rate" value={`${rate}%`} />
            </div>

            <div className="mt-5 pt-5 border-t border-white/8 flex items-center gap-2 justify-end">
                <button onClick={onEdit} data-testid={`campaign-edit-${c.id}`}
                    className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5 hover:text-white">
                    <Edit3 size={12} /> {i18n.t("common.edit")}
                </button>
                <button onClick={onOpen}
                    className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] hover:bg-[var(--jr-brand)]/25">
                    {i18n.t("campaigns.open_btn")} <ArrowRight size={12} />
                </button>
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        running:  { label: "Running",  Icon: Play,   bg: "bg-[var(--jr-brand)]/15",  fg: "text-[var(--jr-brand)]" },
        paused:   { label: "Paused",   Icon: Pause,  bg: "bg-[var(--jr-amber)]/15",  fg: "text-[var(--jr-amber)]" },
        draft:    { label: "Draft",    Icon: Edit3,  bg: "bg-white/5",               fg: "text-[var(--jr-text-2)]" },
        completed:{ label: i18n.t("campaigns.stat_done"),    Icon: CheckCircle2, bg: "bg-[#22c55e]/15",    fg: "text-[#22c55e]" },
    };
    const m = map[status] || map.draft;
    const Icon = m.Icon;
    return (
        <span className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded shrink-0 inline-flex items-center gap-1 ${m.bg} ${m.fg}`}
            data-testid={`status-pill-${status}`}>
            <Icon size={10} /> {m.label}
        </span>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}</div>
            <div className="font-display text-xl tracking-tighter mt-1 text-white">{value}</div>
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════════════
// Campaign Builder Modal
// ═════════════════════════════════════════════════════════════════════════
const EMPTY_STEP = () => ({
    id: "new_" + Math.random().toString(36).slice(2, 10),
    channel: "linkedin_connect",
    delay_days: 0,
    subject: "",
    body: "",
    variables: ["first_name", "company"],
    linkedin_connect_wait_days: 2,
    tone_of_voice: "",
    // Sprint 5 additions
    persona: "",
    persona_role_hint: "",
    is_prewarm: false,
});

function CampaignBuilder({ campaign, defaultType = "sales", onClose, onSaved }) {
    const isEdit = !!campaign;
    const [meta, setMeta] = useState(() => ({
        type: campaign?.type || defaultType || "sales",
        name: campaign?.name || "",
        hunt_mission_ids: campaign?.hunt_mission_ids || [],
        stop_on_reply: campaign?.stop_on_reply ?? true,
        stop_on_meeting: campaign?.stop_on_meeting ?? true,
        stop_on_opt_out: campaign?.stop_on_opt_out ?? true,
        stop_on_stage_won_lost: campaign?.stop_on_stage_won_lost ?? true,
        ai_personalize: campaign?.ai_personalize ?? true,
        tone_of_voice: campaign?.tone_of_voice || "",
        status: campaign?.status || "draft",
    }));
    const [steps, setSteps] = useState(() => (campaign?.steps || []).map((s) => ({ ...s })));
    const [missions, setMissions] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/hunt-missions").then((r) => setMissions(r.data || [])).catch(() => {});
    }, []);

    const addStep = () => setSteps([...steps, EMPTY_STEP()]);
    const removeStep = (idx) => setSteps(steps.filter((_, i) => i !== idx));
    const updateStep = (idx, patch) => setSteps(steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
    const moveUp = (idx) => {
        if (idx === 0) return;
        const next = [...steps];
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
        setSteps(next);
    };
    const moveDown = (idx) => {
        if (idx === steps.length - 1) return;
        const next = [...steps];
        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
        setSteps(next);
    };

    const save = async () => {
        if (!meta.name.trim()) { toast.error(i18n.t("search.name_required")); return; }
        if (steps.length === 0) { toast.error(i18n.t("campaigns.min_step")); return; }
        setSaving(true);
        try {
            const payload = {
                ...meta,
                steps: steps.map(({ id, ...s }) => s),  // strip local ids so server generates new ones
            };
            if (isEdit) {
                await api.patch(`/campaigns/${campaign.id}`, payload);
                toast.success(i18n.t("campaigns.updated_ok"));
            } else {
                await api.post("/campaigns", payload);
                toast.success(i18n.t("campaigns.created_ok"));
            }
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("campaigns.save_failed"));
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-4 md:p-10 overflow-y-auto"
            data-testid="campaign-builder-modal">
            <div className="max-w-4xl mx-auto rounded-2xl border border-white/10 bg-[var(--jr-surface)] shadow-2xl">
                <div className="p-6 border-b border-white/8 flex items-start justify-between gap-3">
                    <div>
                        <h2 className="font-display text-2xl tracking-tight text-white">
                            {isEdit ? i18n.t("campaigns.edit_title") : i18n.t("campaigns.new_campaign")}
                        </h2>
                        <p className="text-xs text-[var(--jr-text-2)] mt-1">
                            {i18n.t("campaigns.builder_sub")}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-[var(--jr-text-3)] hover:text-white" data-testid="builder-close">
                        <X size={22} />
                    </button>
                </div>

                {/* Meta */}
                <div className="p-6 space-y-5 border-b border-white/8">
                    <div className="grid md:grid-cols-[120px_1fr] gap-4">
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Type</label>
                            <select value={meta.type} onChange={(e) => setMeta({ ...meta, type: e.target.value })}
                                data-testid="builder-type"
                                className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                                <option value="sales">Sales</option>
                                <option value="recruitment">Recruitment</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Naam</label>
                            <input value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })}
                                data-testid="builder-name"
                                placeholder="bv. FinTech CTOs Series A cadence"
                                className="mt-2 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none" />
                        </div>
                    </div>

                    {/* Hunt missions */}
                    {missions.length > 0 && (
                        <div className="rounded-lg border border-white/8 bg-white/[0.02] p-4">
                            <div className="text-xs font-medium text-white mb-2">{i18n.t("campaigns.link_missions")}</div>
                            <div className="text-[10px] text-[var(--jr-text-3)] mb-3">{i18n.t("campaigns.link_missions_sub")}</div>
                            <div className="flex flex-wrap gap-1.5">
                                {missions.map((m) => {
                                    const active = meta.hunt_mission_ids.includes(m.id);
                                    return (
                                        <button key={m.id} onClick={() => {
                                            const set = new Set(meta.hunt_mission_ids);
                                            active ? set.delete(m.id) : set.add(m.id);
                                            setMeta({ ...meta, hunt_mission_ids: [...set] });
                                        }} data-testid={`toggle-mission-${m.id}`}
                                            className={`text-[11px] px-2.5 py-1 rounded-full border ${active
                                                ? "bg-[var(--jr-brand)]/20 border-[var(--jr-brand)]/40 text-[var(--jr-brand)]"
                                                : "border-white/10 text-[var(--jr-text-3)] hover:text-white"}`}>
                                            {m.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Stop conditions */}
                    <div className="rounded-lg border border-white/8 bg-white/[0.02] p-4">
                        <div className="text-xs font-medium text-white mb-3 flex items-center gap-2">
                            <StopCircle size={13} className="text-[var(--jr-amber)]" /> Automatisch stoppen wanneer…
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                ["stop_on_reply", "Prospect antwoordt"],
                                ["stop_on_meeting", "Meeting geboekt (via inbox tag)"],
                                ["stop_on_opt_out", "Prospect opt-out"],
                                ["stop_on_stage_won_lost", "Lead → Won of Lost"],
                            ].map(([k, l]) => (
                                <label key={k} className="inline-flex items-center gap-2 text-xs text-[var(--jr-text-2)] cursor-pointer">
                                    <input type="checkbox" checked={meta[k]} onChange={(e) => setMeta({ ...meta, [k]: e.target.checked })}
                                        data-testid={`stop-${k}`} className="accent-[var(--jr-brand)]" />
                                    {l}
                                </label>
                            ))}
                        </div>
                        <p className="text-[10px] text-[var(--jr-text-3)] mt-3">
                            Je kan altijd manueel stoppen per prospect vanuit de campagne-detailpagina.
                        </p>
                    </div>
                </div>

                {/* Steps builder */}
                <div className="p-6 space-y-3 border-b border-white/8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-medium text-white">Cadence steps</h3>
                            <p className="text-[10px] text-[var(--jr-text-3)] mt-0.5">
                                {steps.length} step{steps.length === 1 ? "" : "s"} · gebruik <code className="text-[var(--jr-brand)]">{"{{first_name}}"}</code> en <code className="text-[var(--jr-brand)]">{"{{company}}"}</code> als variabelen.
                            </p>
                        </div>
                        <button onClick={addStep} data-testid="add-step"
                            className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-black font-medium hover:bg-[var(--jr-brand-2)]">
                            <Plus size={12} /> Voeg step toe
                        </button>
                    </div>

                    {/* Visual horizontal timeline preview — always visible so
                        users see the full cadence at a glance while editing */}
                    {steps.length > 0 && (
                        <SequenceTimeline steps={steps} />
                    )}

                    {steps.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-white/15 p-6 text-center text-[var(--jr-text-3)] text-xs">
                            Nog geen steps. Voeg er één toe om te starten.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {steps.map((s, i) => (
                                <StepEditor key={s.id || i} step={s} index={i}
                                    onChange={(patch) => updateStep(i, patch)}
                                    onRemove={() => removeStep(i)}
                                    onUp={() => moveUp(i)}
                                    onDown={() => moveDown(i)}
                                    canUp={i > 0} canDown={i < steps.length - 1} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="p-6 flex items-center justify-end gap-2">
                    <button onClick={onClose} data-testid="builder-cancel"
                        className="px-4 py-2 rounded-full border border-white/10 text-sm text-[var(--jr-text-2)] hover:bg-white/5">
                        Annuleer
                    </button>
                    <button onClick={save} disabled={saving} data-testid="builder-save"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--jr-brand)] text-black text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                        {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                        {isEdit ? i18n.t("campaigns.update_btn") : i18n.t("common.create")}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════════════
// SequenceTimeline — visual horizontal cadence preview
// ═════════════════════════════════════════════════════════════════════════
function SequenceTimeline({ steps }) {
    return (
        <div className="rounded-lg border border-white/10 bg-gradient-to-br from-white/[0.02] to-transparent p-4"
            data-testid="sequence-timeline">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-3">
                Visueel overzicht — cadence flow
            </div>
            <div className="overflow-x-auto">
                <div className="flex items-stretch gap-0 min-w-max py-2">
                    {steps.map((s, i) => {
                        const meta = CHANNEL_META[s.channel] || CHANNEL_META.email;
                        const Icon = meta.Icon;
                        const isLast = i === steps.length - 1;
                        return (
                            <React.Fragment key={s.id || i}>
                                <div className="flex flex-col items-center min-w-[140px] text-center"
                                    data-testid={`timeline-step-${i}`}>
                                    <div className="relative">
                                        <div className="w-14 h-14 rounded-full grid place-items-center border-2 shadow-md"
                                            style={{ background: `${meta.tint}22`, borderColor: meta.tint, color: meta.tint }}>
                                            <Icon size={22}/>
                                        </div>
                                        <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[var(--jr-surface)] border-2 border-[var(--jr-brand)] grid place-items-center text-[10px] font-mono font-semibold text-[var(--jr-brand)]">
                                            {i + 1}
                                        </span>
                                    </div>
                                    <div className="mt-2 text-xs font-medium text-white">{meta.label}</div>
                                    <div className="text-[10px] font-mono text-[var(--jr-text-3)] mt-0.5">
                                        {s.delay_days > 0 ? i18n.t("campaigns.wait_days", { days: s.delay_days }) : i18n.t("campaigns.immediately")}
                                    </div>
                                    {s.channel === "linkedin_connect" && s.linkedin_connect_wait_days > 0 && (
                                        <div className="text-[9px] font-mono text-[var(--jr-amber)] mt-0.5">
                                            <Clock size={8} className="inline mr-0.5"/> wacht {s.linkedin_connect_wait_days}d op accept
                                        </div>
                                    )}
                                    {s.is_prewarm && (
                                        <div className="mt-1 inline-block px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-[0.12em] bg-[var(--jr-brand)]/20 text-[var(--jr-brand)] border border-[var(--jr-brand)]/40" data-testid={`timeline-prewarm-${i}`}>
                                            Pre-warm
                                        </div>
                                    )}
                                    {s.persona && (
                                        <div className="mt-1 text-[9px] font-mono text-[#a855f7]" data-testid={`timeline-persona-${i}`}>
                                            → {s.persona}
                                        </div>
                                    )}
                                    {s.subject && (
                                        <div className="text-[10px] text-[var(--jr-text-2)] mt-1 italic max-w-[140px] truncate" title={s.subject}>
                                            &quot;{s.subject}&quot;
                                        </div>
                                    )}
                                </div>
                                {!isLast && (
                                    <div className="flex-1 min-w-[40px] grid place-items-center pt-7">
                                        <div className="relative w-full h-0.5" style={{ background: "linear-gradient(90deg, var(--jr-brand) 0%, rgba(255,90,0,0.3) 100%)" }}>
                                            <ArrowRight size={16} className="absolute right-[-8px] top-[-7px] text-[var(--jr-brand)]"/>
                                        </div>
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function StepEditor({ step, index, onChange, onRemove, onUp, onDown, canUp, canDown }) {
    const meta = CHANNEL_META[step.channel] || CHANNEL_META.email;
    const [expanded, setExpanded] = useState(true);
    return (
        <div className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] overflow-hidden"
            data-testid={`step-${index}`}>
            <div className="flex items-center gap-2 p-3 border-b border-white/8">
                <div className="flex flex-col text-[var(--jr-text-3)]">
                    <button onClick={onUp} disabled={!canUp} className="hover:text-white disabled:opacity-25 disabled:cursor-not-allowed"><GripVertical size={12} /></button>
                </div>
                <span className="w-6 h-6 rounded-full bg-white/5 grid place-items-center text-[10px] font-mono text-[var(--jr-text-2)] shrink-0">{index + 1}</span>
                <select value={step.channel} onChange={(e) => onChange({ channel: e.target.value })}
                    data-testid={`step-${index}-channel`}
                    className="bg-transparent border border-white/10 rounded-lg px-2 py-1 text-xs text-white">
                    {Object.entries(CHANNEL_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <span className="text-[10px] text-[var(--jr-text-3)] font-mono">delay</span>
                <input type="number" min="0" value={step.delay_days} onChange={(e) => onChange({ delay_days: parseInt(e.target.value || "0", 10) })}
                    data-testid={`step-${index}-delay`}
                    className="w-16 bg-transparent border border-white/10 rounded-lg px-2 py-1 text-xs text-white" />
                <span className="text-[10px] text-[var(--jr-text-3)] font-mono">dagen</span>
                <div className="flex-1" />
                <button onClick={() => setExpanded((v) => !v)} className="p-1 text-[var(--jr-text-3)] hover:text-white">
                    <ChevronDown size={14} className={`transition-transform ${expanded ? "" : "-rotate-90"}`} />
                </button>
                <button onClick={onRemove} data-testid={`step-${index}-remove`}
                    className="p-1 text-[var(--jr-text-3)] hover:text-[var(--jr-error)]">
                    <Trash2 size={14} />
                </button>
            </div>
            {expanded && (
                <div className="p-4 space-y-3">
                    {step.channel === "email" && (
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Subject</label>
                            <input value={step.subject || ""} onChange={(e) => onChange({ subject: e.target.value })}
                                data-testid={`step-${index}-subject`}
                                placeholder="Snelle vraag over {{company}}"
                                className="mt-2 w-full bg-[var(--jr-surface)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none" />
                        </div>
                    )}
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">
                            {step.channel === "linkedin_connect" ? "Connection note (max 300 chars)" : "Body"}
                        </label>
                        <textarea value={step.body} onChange={(e) => onChange({ body: e.target.value })}
                            data-testid={`step-${index}-body`}
                            rows={step.channel === "linkedin_connect" ? 3 : 5}
                            maxLength={step.channel === "linkedin_connect" ? 300 : 3000}
                            placeholder="Hey {{first_name}}, ik zag {{signal_summary}}…"
                            className="mt-2 w-full bg-[var(--jr-surface)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none font-mono" />
                        <div className="text-[10px] text-[var(--jr-text-3)] mt-1">
                            {step.channel === "linkedin_connect" && <>{step.body.length}/300 · </>}
                            AI personaliseert dit per prospect met tone-of-voice · human reviewt voor send.
                        </div>
                    </div>
                    {step.channel === "linkedin_connect" && (
                        <div className="flex items-center gap-2 rounded-md bg-white/5 px-3 py-2 text-[11px] text-[var(--jr-text-2)]">
                            <Clock size={12} className="text-[var(--jr-amber)]" />
                            Wacht
                            <input type="number" min="1" max="14" value={step.linkedin_connect_wait_days}
                                onChange={(e) => onChange({ linkedin_connect_wait_days: parseInt(e.target.value || "2", 10) })}
                                data-testid={`step-${index}-wait`}
                                className="w-12 bg-transparent border border-white/10 rounded px-1.5 py-0.5 text-xs text-white" />
                            dagen op accept · daarna sequence continueert (fallback via volgend kanaal).
                        </div>
                    )}
                    {/* Sprint 5 · #16 Persona / #17 Pre-warm */}
                    <div className="pt-3 border-t border-white/8 space-y-2">
                        <div className="grid sm:grid-cols-2 gap-2">
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Persona (optioneel)</label>
                                <select value={step.persona || ""} onChange={(e) => onChange({ persona: e.target.value })}
                                    data-testid={`step-${index}-persona`}
                                    className="mt-1 w-full bg-[var(--jr-surface)] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white outline-none">
                                    <option value="">— Primair contact —</option>
                                    <option value="cto">CTO / VP Engineering</option>
                                    <option value="ceo">CEO / Founder</option>
                                    <option value="cfo">CFO / Finance lead</option>
                                    <option value="head_of_talent">Head of Talent / HRD</option>
                                    <option value="head_of_sales">Head of Sales / CRO</option>
                                    <option value="head_of_product">Head of Product</option>
                                    <option value="head_of_ops">Head of Ops / COO</option>
                                    <option value="custom">Aangepast…</option>
                                </select>
                            </div>
                            {step.persona === "custom" && (
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Rol-hint (bv. Head of Data)</label>
                                    <input value={step.persona_role_hint || ""} onChange={(e) => onChange({ persona_role_hint: e.target.value })}
                                        data-testid={`step-${index}-persona-hint`}
                                        placeholder="head of data"
                                        className="mt-1 w-full bg-[var(--jr-surface)] border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white outline-none"/>
                                </div>
                            )}
                        </div>
                        {step.persona && (
                            <div className="text-[10px] text-[var(--jr-text-3)] italic">
                                Scheduler kiest een stakeholder wiens rol overeenkomt met deze persona. Val terug op primair contact als geen match.
                            </div>
                        )}
                        <label className="flex items-center gap-2 rounded-md bg-[var(--jr-brand)]/[0.06] border border-[var(--jr-brand)]/20 px-3 py-2 text-xs text-white cursor-pointer">
                            <input type="checkbox" checked={!!step.is_prewarm}
                                onChange={(e) => onChange({ is_prewarm: e.target.checked })}
                                data-testid={`step-${index}-prewarm`}
                                className="accent-[var(--jr-brand)]"/>
                            <span className="flex-1">Pre-warm stap (like/comment op recente post)</span>
                            <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--jr-brand)]">manueel</span>
                        </label>
                        {step.is_prewarm && (
                            <div className="text-[10px] text-[var(--jr-text-3)] italic">
                                Deze stap verstuurt géén DM. Scheduler creëert een reminder in de outreach-lijst zodat je manueel warm-up doet vóór de cold contact.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ═════════════════════════════════════════════════════════════════════════
// Campaign Detail Drawer
// ═════════════════════════════════════════════════════════════════════════
function CampaignDetailDrawer({ campaignId, onClose, onEdit, onChanged }) {
    const [c, setC] = useState(null);
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [a, b] = await Promise.all([
                api.get(`/campaigns/${campaignId}`),
                api.get(`/campaigns/${campaignId}/enrollments?limit=200`),
            ]);
            setC(a.data);
            setEnrollments(b.data || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [campaignId]);

    const startCampaign = async () => {
        setBusy(true);
        try { await api.post(`/campaigns/${campaignId}/start`); toast.success(i18n.t("campaigns.started_ok")); await load(); onChanged?.(); }
        catch (e) { toast.error(e?.response?.data?.detail || i18n.t("campaigns.start_failed")); }
        finally { setBusy(false); }
    };
    const pauseCampaign = async () => {
        setBusy(true);
        try { await api.post(`/campaigns/${campaignId}/pause`); toast.success(i18n.t("campaigns.paused_ok")); await load(); onChanged?.(); }
        catch (e) { toast.error(e?.response?.data?.detail || i18n.t("campaigns.pause_failed")); }
        finally { setBusy(false); }
    };
    const removeCampaign = async () => {
        if (!confirm(i18n.t("campaigns.delete_confirm"))) return;
        setBusy(true);
        try { await api.delete(`/campaigns/${campaignId}`); toast.success(i18n.t("common.deleted")); onClose?.(); onChanged?.(); }
        catch (e) { toast.error(e?.response?.data?.detail || i18n.t("campaigns.delete_failed")); }
        finally { setBusy(false); }
    };
    const stopEnrolment = async (id) => {
        try { await api.post(`/enrollments/${id}/stop`, { reason: "manual" }); toast.success(i18n.t("campaigns.enrol_stopped")); await load(); }
        catch (e) { toast.error(e?.response?.data?.detail || i18n.t("campaigns.stop_failed")); }
    };
    const pauseEnrolment = async (id) => {
        try { await api.post(`/enrollments/${id}/pause`); toast.success(i18n.t("campaigns.paused_short")); await load(); }
        catch (e) { toast.error(e?.response?.data?.detail || i18n.t("campaigns.pause_failed")); }
    };
    const resumeEnrolment = async (id) => {
        try { await api.post(`/enrollments/${id}/resume`); toast.success(i18n.t("campaigns.resumed_ok")); await load(); }
        catch (e) { toast.error(e?.response?.data?.detail || i18n.t("campaigns.resume_failed")); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <div className="fixed right-0 top-0 bottom-0 w-full max-w-3xl bg-[var(--jr-surface)] border-l border-white/10 shadow-2xl overflow-y-auto"
                onClick={(e) => e.stopPropagation()} data-testid="campaign-detail-drawer">
                {loading || !c ? (
                    <div className="p-10 text-center text-[var(--jr-text-2)]"><Loader2 className="animate-spin mx-auto" size={20} /></div>
                ) : (
                    <>
                        <div className="p-6 border-b border-white/8 flex items-start justify-between gap-3 sticky top-0 bg-[var(--jr-surface)] z-10">
                            <div className="min-w-0">
                                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] flex items-center gap-2">
                                    {c.type} · <StatusPill status={c.status} />
                                </div>
                                <h2 className="font-display text-2xl tracking-tight text-white truncate mt-1">{c.name}</h2>
                                {c.description && <p className="text-xs text-[var(--jr-text-2)] mt-1">{c.description}</p>}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                {c.status === "running" ? (
                                    <button onClick={pauseCampaign} disabled={busy} data-testid="detail-pause"
                                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[var(--jr-amber)]/40 text-[var(--jr-amber)] hover:bg-[var(--jr-amber)]/10">
                                        <Pause size={12} /> {i18n.t("campaigns.st_pause")}
                                    </button>
                                ) : (
                                    <button onClick={startCampaign} disabled={busy || (c.steps || []).length === 0} data-testid="detail-start"
                                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-black hover:bg-[var(--jr-brand-2)] disabled:opacity-50">
                                        <Play size={12} /> Start
                                    </button>
                                )}
                                <button onClick={() => onEdit?.(c)} data-testid="detail-edit"
                                    className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-white hover:bg-white/5">
                                    <Edit3 size={14} />
                                </button>
                                <button onClick={removeCampaign} disabled={busy} data-testid="detail-delete"
                                    className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-[var(--jr-error)] hover:bg-[var(--jr-error)]/10">
                                    <Trash2 size={14} />
                                </button>
                                <button onClick={onClose} className="text-[var(--jr-text-3)] hover:text-white p-1"><X size={18} /></button>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-3 border-b border-white/8">
                            <Stat label="Steps" value={(c.steps || []).length} />
                            <Stat label="Enrolled" value={c.stats?.enrolled || 0} />
                            <Stat label={i18n.t("campaigns.stat_active")} value={c.stats?.running || 0} />
                            <Stat label={i18n.t("campaigns.stat_done")} value={c.stats?.completed || 0} />
                            <Stat label="Reply-rate" value={`${c.contacted ? Math.round((c.replied / c.contacted) * 100) : 0}%`} />
                            <Stat label="Meetings" value={c.meetings_booked || 0} />
                            <Stat label={i18n.t("campaigns.stat_stopped")} value={c.stats?.stopped || 0} />
                            <Stat label={i18n.t("campaigns.stat_paused")} value={c.stats?.paused || 0} />
                        </div>

                        {/* Sequence timeline */}
                        <div className="p-6 border-b border-white/8">
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-3">Sequence — visueel</div>
                            {(c.steps || []).length > 0 && <SequenceTimeline steps={c.steps || []} />}
                            <div className="mt-4 space-y-2">
                                {(c.steps || []).map((s, i) => {
                                    const m = CHANNEL_META[s.channel] || CHANNEL_META.email;
                                    const Icon = m.Icon;
                                    return (
                                        <div key={s.id || i} className="rounded-lg border border-white/8 bg-white/[0.02] p-3 flex items-start gap-3"
                                            data-testid={`detail-step-${i}`}>
                                            <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: `${m.tint}22`, color: m.tint }}>
                                                <Icon size={14} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-xs font-medium text-white">{i + 1}. {m.label}</span>
                                                    <span className="text-[10px] font-mono text-[var(--jr-text-3)]">+{s.delay_days}d</span>
                                                    {s.channel === "linkedin_connect" && (
                                                        <span className="text-[10px] font-mono text-[var(--jr-amber)] inline-flex items-center gap-1">
                                                            <Clock size={9} /> wait {s.linkedin_connect_wait_days}d
                                                        </span>
                                                    )}
                                                </div>
                                                {s.subject && <div className="text-xs text-[var(--jr-text-2)] mt-1 italic">Subject: {s.subject}</div>}
                                                {s.body && <div className="text-xs text-[var(--jr-text-2)] mt-1 whitespace-pre-wrap line-clamp-3">{s.body}</div>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Enrolments */}
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Enrolments</div>
                                    <div className="text-sm text-white">{enrollments.length} prospect{enrollments.length === 1 ? "" : "s"}</div>
                                </div>
                            </div>
                            {enrollments.length === 0 ? (
                                <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-[var(--jr-text-3)] text-xs">
                                    Geen prospects enrolled. Voeg toe vanuit de Pipeline (klik lead → drawer → &quot;Voeg toe aan campagne&quot;).
                                </div>
                            ) : (
                                <div className="space-y-1.5">
                                    {enrollments.map((e) => (
                                        <EnrolmentRow key={e.id} e={e} steps={c.steps || []}
                                            onStop={() => stopEnrolment(e.id)}
                                            onPause={() => pauseEnrolment(e.id)}
                                            onResume={() => resumeEnrolment(e.id)} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function EnrolmentRow({ e, steps, onStop, onPause, onResume }) {
    const stepIdx = Math.min(e.current_step_index || 0, steps.length - 1);
    const step = steps[stepIdx];
    const meta = step ? CHANNEL_META[step.channel] : null;
    const statusStyle = {
        queued:            { label: i18n.t("campaigns.st_queued"),     tint: "text-[var(--jr-text-3)]", bg: "bg-white/5" },
        running:           { label: i18n.t("campaigns.stat_active"),       tint: "text-[var(--jr-brand)]",  bg: "bg-[var(--jr-brand)]/10" },
        waiting_delay:     { label: "Delay",        tint: "text-[var(--jr-info)]",   bg: "bg-[var(--jr-info)]/10" },
        waiting_for_accept:{ label: i18n.t("campaigns.st_wait_accept"), tint: "text-[var(--jr-amber)]",  bg: "bg-[var(--jr-amber)]/10" },
        awaiting_approval: { label: "Review",       tint: "text-[var(--jr-amber)]",  bg: "bg-[var(--jr-amber)]/10" },
        completed:         { label: i18n.t("campaigns.stat_done"),        tint: "text-[#22c55e]",           bg: "bg-[#22c55e]/10" },
        stopped:           { label: i18n.t("campaigns.stat_stopped"),      tint: "text-[var(--jr-error)]",  bg: "bg-[var(--jr-error)]/10" },
        paused:            { label: i18n.t("campaigns.st_pause"),        tint: "text-[var(--jr-text-2)]", bg: "bg-white/5" },
    }[e.status] || { label: e.status, tint: "text-[var(--jr-text-3)]", bg: "bg-white/5" };
    return (
        <div className="rounded-lg border border-white/8 bg-white/[0.02] p-3 flex items-center gap-3 hover:border-white/16"
            data-testid={`enrolment-${e.id}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-white truncate">{e.contact_name || e.company_name || "—"}</span>
                    {e.company_name && e.contact_name && <span className="text-[10px] text-[var(--jr-text-3)] truncate">@ {e.company_name}</span>}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`text-[10px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded ${statusStyle.bg} ${statusStyle.tint}`}
                        data-testid={`enrolment-status-${e.id}`}>
                        {statusStyle.label}
                    </span>
                    {meta && step && (
                        <span className="text-[10px] text-[var(--jr-text-3)] inline-flex items-center gap-1">
                            <meta.Icon size={9} /> Step {stepIdx + 1}/{steps.length} · {meta.label}
                        </span>
                    )}
                    {e.stop_reason && <span className="text-[10px] text-[var(--jr-error)]">· {e.stop_reason}</span>}
                    {e.enrolled_via && e.enrolled_via !== "manual" && (
                        <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-white/5 text-[var(--jr-text-3)]">{e.enrolled_via}</span>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                {e.status === "paused" ? (
                    <button onClick={onResume} data-testid={`enrolment-resume-${e.id}`} title="Hervat"
                        className="p-1.5 rounded text-[var(--jr-info)] hover:bg-[var(--jr-info)]/10"><Play size={12} /></button>
                ) : ["queued", "running", "waiting_delay", "waiting_for_accept", "awaiting_approval"].includes(e.status) && (
                    <button onClick={onPause} data-testid={`enrolment-pause-${e.id}`} title="Pauze"
                        className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-white hover:bg-white/5"><Pause size={12} /></button>
                )}
                {!["stopped", "completed"].includes(e.status) && (
                    <button onClick={onStop} data-testid={`enrolment-stop-${e.id}`} title="Stop permanent"
                        className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-[var(--jr-error)] hover:bg-[var(--jr-error)]/10"><StopCircle size={12} /></button>
                )}
            </div>
        </div>
    );
}
